import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef } from 'react';

export interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'rows'> {
  autoResize?: boolean;
  maxHeight?: number;
  minHeight?: number;
}

export interface TextAreaRef {
  focus: () => void;
  blur: () => void;
  reset: () => void;
}

const TextArea = forwardRef<TextAreaRef, TextAreaProps>(({
  autoResize = false,
  maxHeight = 300,
  minHeight = 24.7,
  className = '',
  onChange,
  value,
  ...props
}, ref) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const adjustHeight = useCallback(() => {
    const textarea = textareaRef.current;
    if (textarea && autoResize) {
      textarea.style.height = 'auto';
      const newHeight = Math.min(Math.max(textarea.scrollHeight, minHeight), maxHeight);
      textarea.style.height = `${newHeight}px`;
    }
  }, [autoResize, maxHeight, minHeight]);

  useEffect(() => {
    adjustHeight();
  }, [value, autoResize, maxHeight, minHeight, adjustHeight]);

  useImperativeHandle(ref, () => ({
    focus: () => textareaRef.current?.focus(),
    blur: () => textareaRef.current?.blur(),
    reset: () => {
      if (textareaRef.current && autoResize) {
        textareaRef.current.style.height = 'auto';
      }
    }
  }));

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    onChange?.(e);
    adjustHeight();
  };

  const baseClasses = [
    'w-full',
    'resize-none',
    'border-none',
    'outline-none',
    'bg-transparent',
    'text-content-primary',
    'font-manrope',
    'font-medium',
    'placeholder:text-content-tertiary',
    'transition-all',
    'duration-200',
    'disabled:cursor-not-allowed',
    'disabled:opacity-50'
  ];

  return (
    <textarea
      ref={textareaRef}
      value={value}
      onChange={handleChange}
      className={[...baseClasses, className].filter(Boolean).join(' ')}
      style={{
        minHeight: `${minHeight}px`,
        maxHeight: autoResize ? `${maxHeight}px` : undefined,
        overflow: autoResize ? 'auto' : undefined
      }}
      rows={autoResize ? undefined : 1}
      {...props}
    />
  );
});

TextArea.displayName = 'TextArea';

export default TextArea;